/**
 * Journey: IVF, With Partner, Male
 *
 * Steps: Learn > Test > Clinic > Prep > Treatment > Wait
 *
 * Same 6-step structure as the female IVF journey, reframed for the male partner.
 * The male provides the sperm sample; the female partner undergoes stimulation,
 * retrieval, and transfer. Content is written from the male's perspective.
 */

import type { JourneyDefinition, PlanStep } from "../plan.types";

const learnStep: PlanStep = {
  id: "learn",
  title: "How IVF Works",
  subtitle: "The most effective fertility treatment",
  icon: "BookOpen",
  education: {
    context:
      "IVF (in vitro fertilization) creates embryos outside the body, selects the best one, and transfers it to your partner's uterus. It's the most effective fertility treatment, with success rates of 40-50% per transfer for women under 35.",
    details:
      "The full process:\n1. Ovarian stimulation (10-14 days): Your partner takes injectable medications to mature multiple eggs\n2. Egg retrieval (20 minutes): Eggs are collected under light sedation\n3. Your sperm sample (retrieval day): You provide a fresh sample at the clinic\n4. Fertilization (day 0): Eggs and sperm are combined in the lab (or ICSI is used)\n5. Embryo culture (5-7 days): Embryos develop to blastocyst stage\n6. Embryo transfer (5 minutes): A single embryo is placed in your partner's uterus\n7. Two-week wait: You both wait ~14 days for a blood pregnancy test (beta hCG)\n\nYour role: sperm sample on retrieval day, emotional support throughout, and being present at key appointments.",
    learnMoreLink: "ivf-guide",
  },
  actions: [
    {
      label: "I understand this",
      variant: "primary",
      icon: "Check",
      signal: "complete-step",
    },
    {
      label: "Full IVF guide",
      variant: "outline",
      icon: "BookOpen",
      screen: "LearnScreen",
      params: { article: "ivf-guide" },
    },
  ],
  completionSignal: { type: "manual" },
  conditionModifiers: [
    {
      condition: "male-factor",
      additionalEducation:
        "Your semen analysis suggests male factor infertility. Your clinic will likely recommend ICSI (intracytoplasmic sperm injection), where a single healthy sperm is injected directly into each egg. ICSI bypasses natural fertilization barriers and is very effective: fertilization rates are 70-80%.",
    },
    {
      condition: "age-38-plus",
      additionalEducation:
        "After 40, sperm DNA fragmentation increases, which can affect embryo quality and miscarriage rates. Your clinic may recommend DNA fragmentation testing. Lifestyle optimization (avoiding heat, limiting alcohol, supplements) can help reduce fragmentation.",
    },
  ],
  ageSpecificContent: [
    {
      range: [0, 39],
      content:
        "Male age has a modest effect on IVF outcomes. Sperm quality is generally well-maintained through your 30s.",
    },
    {
      range: [40, 44],
      content:
        "After 40, sperm DNA fragmentation increases slightly. Your clinic may recommend a DNA fragmentation test alongside the standard semen analysis.",
    },
    {
      range: [45, 99],
      content:
        "Over 45, sperm DNA fragmentation is higher and time-to-pregnancy increases. Your clinic may discuss DNA fragmentation testing and whether ICSI would benefit your cycle.",
    },
  ],
};

const testStep: PlanStep = {
  id: "test",
  title: "Baseline Testing for Both of You",
  subtitle: "Comprehensive fertility workup",
  icon: "TestTube",
  education: {
    context:
      "IVF requires testing for both partners. Your semen analysis determines the fertilization method (conventional vs. ICSI), while your partner's tests help plan the stimulation protocol.",
  },
  actions: [
    {
      label: "Find a lab near you",
      variant: "primary",
      screen: "FindCareScreen",
    },
    {
      label: "Upload test results",
      variant: "outline",
      screen: "DocumentsScreen",
    },
  ],
  substeps: [
    {
      id: "semen-analysis",
      title: "Semen analysis",
      education:
        "Measures count, motility, and morphology. Results determine if conventional fertilization or ICSI is needed. Abstain 2-5 days before your appointment.",
      status: "not-started",
    },
    {
      id: "dna-fragmentation",
      title: "DNA fragmentation test (if recommended)",
      education:
        "Measures sperm DNA damage. High fragmentation (>30%) may warrant lifestyle changes or ICSI. Not always needed. Your clinic will advise.",
      status: "not-started",
    },
    {
      id: "infectious-disease",
      title: "Infectious disease screening",
      education:
        "Required for both partners before any IVF cycle. Standard panel: HIV, Hepatitis B/C, syphilis, gonorrhea, chlamydia.",
      status: "not-started",
    },
  ],
  partnerTrack: {
    education:
      "Your partner's ovarian reserve and uterine health determine the stimulation protocol and transfer strategy.",
    substeps: [
      {
        id: "partner-amh",
        title: "Partner: AMH (Anti-Müllerian Hormone)",
        education:
          "Measures egg supply. The single most important test for protocol planning.",
        status: "not-started",
        isPartnerTrack: true,
      },
      {
        id: "partner-fsh-estradiol",
        title: "Partner: FSH + Estradiol",
        education: "Cycle day 2-3 blood test. Shows ovarian function.",
        status: "not-started",
        isPartnerTrack: true,
      },
      {
        id: "partner-afc",
        title: "Partner: Antral Follicle Count (AFC)",
        education:
          "Transvaginal ultrasound. Combined with AMH, predicts stimulation response.",
        status: "not-started",
        isPartnerTrack: true,
      },
      {
        id: "partner-hsg",
        title: "Partner: HSG (Hysterosalpingogram)",
        education:
          "X-ray to check fallopian tubes and uterine cavity. While IVF bypasses the tubes, this reveals uterine abnormalities that affect implantation.",
        status: "not-started",
        isPartnerTrack: true,
      },
      {
        id: "partner-infectious",
        title: "Partner: Infectious disease screening",
        education: "Required for both partners before IVF. Standard panel.",
        status: "not-started",
        isPartnerTrack: true,
      },
    ],
  },
  completionSignal: { type: "all-substeps" },
  optimizationWindow: {
    substepId: "semen-analysis",
    biologicalCycle: "~74 days for full sperm regeneration",
    baselineAction: "Get a baseline semen analysis",
    optimizationPeriod: "2-3 months",
    retestAction: "Retest to see improvement",
    lifestyle: [
      {
        title: "Avoid excessive heat",
        evidence: "strong",
        impact: "high",
        education:
          "Sperm production requires temperatures 2-4°C below body temperature. Hot tubs, saunas, tight underwear, and laptops on your lap all raise scrotal temperature.",
      },
      {
        title: "Limit alcohol",
        evidence: "strong",
        impact: "moderate",
        education:
          "Heavy alcohol use reduces testosterone and impairs sperm production. Limiting intake during your optimization window is prudent.",
      },
      {
        title: "Exercise regularly",
        evidence: "moderate",
        impact: "moderate",
        education:
          "Regular moderate exercise improves sperm parameters. Avoid extreme endurance exercise and heavy cycling, which can temporarily lower counts.",
      },
      {
        title: "Quit smoking/vaping",
        evidence: "strong",
        impact: "high",
        education:
          "Tobacco reduces sperm count by 20-30% and increases DNA fragmentation. Cannabis also impairs motility and morphology.",
      },
    ],
    supplements: [
      {
        title: "CoQ10 (200-400mg/day)",
        evidence: "moderate",
        impact: "moderate",
        education:
          "Supports mitochondrial function in sperm cells. Multiple studies show improvements in count and motility after 3 months.",
        action: {
          label: "Add to supplements",
          variant: "outline",
          screen: "SupplementsScreen",
        },
      },
      {
        title: "Zinc (30mg/day)",
        evidence: "moderate",
        impact: "moderate",
        education:
          "Essential for testosterone production and sperm development. Seminal fluid has 100x the zinc concentration of blood.",
        action: {
          label: "Add to supplements",
          variant: "outline",
          screen: "SupplementsScreen",
        },
      },
      {
        title: "Folate/L-methylfolate (400-800mcg/day)",
        evidence: "moderate",
        impact: "moderate",
        education:
          "Supports DNA synthesis in developing sperm. Combined with zinc, studies show improved count.",
        action: {
          label: "Add to supplements",
          variant: "outline",
          screen: "SupplementsScreen",
        },
      },
      {
        title: "Vitamin D (2000-4000 IU/day)",
        evidence: "moderate",
        impact: "low",
        education:
          "Vitamin D receptors exist on sperm cells. Deficiency is associated with lower motility.",
        action: {
          label: "Add to supplements",
          variant: "outline",
          screen: "SupplementsScreen",
        },
      },
    ],
    ageGuidance: [
      {
        bracket: "Under 40",
        range: [0, 39],
        recommendation: "optimize-then-start",
        explanation:
          "You have time to optimize. A 2-3 month window of supplements and lifestyle changes can meaningfully improve sperm quality before your IVF cycle.",
      },
      {
        bracket: "40-45",
        range: [40, 45],
        recommendation: "parallel",
        explanation:
          "Start optimizing now while your partner begins her testing and protocol planning. The timing often aligns well: her prep takes 1-2 months too.",
      },
      {
        bracket: "Over 45",
        range: [46, 99],
        recommendation: "start-now-optimize-alongside",
        explanation:
          "Don't delay your partner's cycle for optimization. Start supplements and lifestyle changes now, but proceed with the cycle on your partner's timeline.",
      },
    ],
  },
};

const clinicStep: PlanStep = {
  id: "clinic",
  title: "Find Your Clinic",
  subtitle: "Success rates matter most for IVF",
  icon: "Building2",
  education: {
    context:
      "For IVF, clinic success rates matter significantly. Check SART.org for clinic-specific data. Key metrics: live birth rate per transfer (not just pregnancy rate), and rates for your partner's age bracket. You'll both attend the initial consultation.",
  },
  actions: [
    {
      label: "Browse clinics near you",
      variant: "primary",
      screen: "FindCareScreen",
      params: { filter: "ivf" },
    },
    {
      label: "Compare clinic costs",
      variant: "outline",
      screen: "CostsOverviewScreen",
    },
  ],
  substeps: [
    {
      id: "research-clinics",
      title: "Research clinics",
      education: "Compare SART success rates for your partner's age bracket.",
      status: "not-started",
    },
    {
      id: "schedule-consultation",
      title: "Schedule a consultation",
      education:
        "30-60 minute initial consultation to review both of your results.",
      status: "not-started",
    },
    {
      id: "choose-clinic",
      title: "Choose your clinic",
      education:
        "Your clinic will start the onboarding process for both of you.",
      status: "not-started",
    },
  ],
  completionSignal: { type: "manual" },
};

const prepStep: PlanStep = {
  id: "prep",
  title: "Get Ready for Your Cycle",
  subtitle: "Pre-cycle paperwork and decisions",
  icon: "ClipboardCheck",
  education: {
    context:
      "IVF prep involves medical history from both partners, insurance, and important decisions about genetic testing and transfer strategy.",
  },
  actions: [
    {
      label: "Continue medical history",
      variant: "primary",
      screen: "MedicalHistory",
    },
    {
      label: "Upload insurance card",
      variant: "secondary",
      screen: "CoverageFlow",
    },
  ],
  substeps: [
    {
      id: "medical-history",
      title: "Complete your medical history",
      education:
        "Your health history is needed for consent forms and to identify any factors that affect sperm quality.",
      status: "not-started",
    },
    {
      id: "insurance-card",
      title: "Upload insurance card",
      education: "Needed for billing.",
      status: "not-started",
    },
    {
      id: "pgta-decision",
      title: "PGT-A decision",
      education:
        "Decide whether to genetically test embryos before transfer. Cost: $3,000-$5,000. Recommended if: your partner is 35+, history of miscarriage, or prior failed transfers.",
      status: "not-started",
    },
    {
      id: "transfer-plan",
      title: "Transfer plan discussion",
      education:
        "Fresh transfer (same cycle) vs frozen transfer (separate cycle). Your clinic will recommend based on your partner's response to stimulation and embryo quality.",
      status: "not-started",
    },
    {
      id: "consent-forms",
      title: "Sign consent forms",
      education:
        "Both partners sign. Covers procedure, risks, and embryo disposition.",
      status: "not-started",
    },
    {
      id: "supplements",
      title: "Start supplements for sperm quality (optional)",
      education:
        "CoQ10, zinc, folate, and vitamin D support sperm quality. Start 2-3 months before if possible. Sperm takes ~74 days to regenerate.",
      status: "not-started",
      actions: [
        {
          label: "See supplements",
          variant: "outline",
          screen: "SupplementsScreen",
        },
      ],
    },
  ],
  partnerTrack: {
    education:
      "Your partner's medical history and prenatal preparation are important for her treatment protocol.",
    substeps: [
      {
        id: "partner-medical-history",
        title: "Partner: complete medical history",
        education: "Needed for her stimulation protocol and consent forms.",
        status: "not-started",
        isPartnerTrack: true,
      },
      {
        id: "partner-prenatal-vitamins",
        title: "Partner: start prenatal vitamins",
        education:
          "Folate and prenatal vitamins are recommended before and during IVF.",
        status: "not-started",
        isPartnerTrack: true,
      },
    ],
  },
  completionSignal: {
    type: "completion-percent",
    field: "prepReadiness",
    threshold: 80,
  },
};

const treatmentStep: PlanStep = {
  id: "treatment",
  title: "Your IVF Cycle",
  subtitle: "Active treatment with daily guidance",
  icon: "Syringe",
  education: {
    context:
      "Your partner's IVF cycle is active. Track appointments and supporting actions in the Care section.",
    details:
      "Your partner's cycle progresses through several phases:\n\n• Baseline (2-3 days): Her ovaries at rest\n• Stimulation (10-14 days): Daily injections, frequent monitoring\n• Trigger: Final maturation shot, precisely timed\n• Retrieval (~20 min): Eggs collected under sedation. You provide your sperm sample that day\n• Fertilization & culture (5-7 days): Embryos develop in the lab\n• Transfer (~5 min): Embryo placed in her uterus\n• Two-week wait: Before the pregnancy blood test\n\nYour key moments: supporting her through stimulation, providing your sample on retrieval day, being there for the transfer.\n\nYour Care page has the shared appointment calendar.",
  },
  actions: [
    {
      label: "Go to Care",
      variant: "primary",
      icon: "Heart",
      screen: "CareOverview",
    },
    { label: "Set your start date", variant: "outline", icon: "Calendar" },
  ],
  completionSignal: { type: "external" },
};

const waitStep: PlanStep = {
  id: "wait",
  title: "The Two-Week Wait",
  subtitle: "Between transfer and pregnancy test",
  icon: "Clock",
  education: {
    context:
      "The two-week wait (TWW) is the period between embryo transfer and the pregnancy blood test.",
    details:
      "Here's what's happening:\nDays 1-3: The embryo hatches and begins attaching to your partner's uterine lining\nDays 4-5: Implantation begins\nDays 6-8: hCG hormone starts being produced if implantation is successful\nDays 9-14: hCG levels rise enough to be detectable by blood test\n\nHow you can help:\n• Be patient: your partner may be anxious, emotional, or withdrawn. All of that is normal\n• Don't read too much into symptoms: cramping, spotting, and breast tenderness can mean pregnant OR not pregnant\n• Help with practical things: meals, errands, keeping life low-stress\n• Avoid pushing for home pregnancy tests before day 10. They're unreliable this early\n• The blood test (beta hCG) is the only reliable answer\n\nThis is a tough wait for both of you. Your support matters more than you might think.",
  },
  actions: [
    {
      label: "Log how you're feeling today",
      variant: "primary",
      icon: "Heart",
    },
    { label: "Support resources", variant: "secondary", icon: "Users" },
    { label: "When is the blood test?", variant: "outline", icon: "Calendar" },
  ],
  completionSignal: { type: "external" },
};

export const IVF_PARTNER_MALE: JourneyDefinition = {
  key: "ivf-with-partner-male",
  gender: "male",
  goal: "pregnancy",
  pathway: "with-partner",
  title: "IVF",
  description: "In vitro fertilization with your partner",
  steps: [learnStep, testStep, clinicStep, prepStep, treatmentStep, waitStep],
};
